// Minimal pub-sub store — the whole app re-renders from this on every
// change (small app, simplicity over a diffing layer).
import { LANGUAGES, detectBrowserLanguage } from './i18n/languages.js';

const listeners = new Set();

// Chain support: Tron + Ethereum only (CLAUDE.md/feature-backend.md item 4)
// — Polygon/Base dropped. USDC/EURC are Circle-issued and not natively on
// Tron; EURT is Ethereum-only per Tether's own supported-protocols listing;
// CNHT is on both (see TOKEN_CONTRACTS' sourcing note below).
const CHAINS = {
  USDC: ['Ethereum · ERC-20'],
  EURC: ['Ethereum · ERC-20'],
  USDT: ['Tron · TRC-20', 'Ethereum · ERC-20'],
  EURT: ['Ethereum · ERC-20'],
  CNHT: ['Ethereum · ERC-20', 'Tron · TRC-20']
};
const NET = {
  USDC: 'Ethereum · ERC-20', EURC: 'Ethereum · ERC-20', USDT: 'Tron · TRC-20',
  EURT: 'Ethereum · ERC-20', CNHT: 'Ethereum · ERC-20'
};
// Gasless-capable token/chain pairs (feature-outstanding.md "make it
// gasless", 2026-08-07) — live-verified against real broadcasts on both
// networks, not assumed (see DECISIONS.md). GasFree (Tron) is USDT-only,
// confirmed empirically against its own authenticated API on both Nile and
// mainnet; Pimlico's ERC-20 paymaster (Ethereum) covers both USDT and USDC.
// EURT/EURC/CNHT have no confirmed gasless path on either chain — a real
// MVP limitation, not an oversight (Circle Paymaster would cover USDC at
// 0% but is incompatible with this WDK version's EntryPoint v0.7; GasFree
// has no USDC support to check against at all).
const GASLESS = {
  USDT: ['Tron · TRC-20', 'Ethereum · ERC-20'],
  USDC: ['Ethereum · ERC-20']
};
export function isGaslessCapable(ccy, chain) {
  return (GASLESS[ccy] || []).includes(chain);
}
// Official token contract addresses per feature-backend.md item 4 — never
// hardcoded from memory; each cross-verified against two independent
// sources (issuer's own site + a block explorer) before being pinned here.
// USDT: tether.to/en/supported-protocols + etherscan.io/tronscan API.
// USDC/EURC: circle.com + developers.circle.com + etherscan.io (Circle
// isn't on Tron — no native USDC/EURC contract exists there).
// EURT/CNHT: tether.to/en/supported-protocols + etherscan.io (EURT has no
// Tron listing on Tether's own page; CNHT has both). Note: Tether's page
// states CNHT's redemption obligation ends 2027-02-20 — not a technical
// blocker, but worth surfacing in UI copy eventually (see DECISIONS.md).
// Ethereum addresses stored all-lowercase deliberately, not EIP-55
// mixed-case — lowercase is always protocol-valid and unambiguous; a
// hand-typed mixed-case checksum that's even one character wrong makes
// strict validators (e.g. ethers.js's getAddress()) throw on an otherwise-
// correct address, which is a worse failure mode than no checksum at all.
// Callers needing checksummed display formatting should compute it at the
// point of use.
const TOKEN_CONTRACTS = {
  USDT: {
    'Ethereum · ERC-20': '0xdac17f958d2ee523a2206206994597c13d831ec7',
    'Tron · TRC-20': 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'
  },
  USDC: {
    'Ethereum · ERC-20': '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
  },
  EURT: {
    'Ethereum · ERC-20': '0xc581b735a1688071a1746c968e0798d642ede491'
  },
  EURC: {
    'Ethereum · ERC-20': '0x1abaea1f7c830bd89acc67ec4af516284b1bc33c'
  },
  CNHT: {
    'Ethereum · ERC-20': '0x6e109e9dd7fa1a58bc3eff667e8e41fc3cc07aef',
    'Tron · TRC-20': 'TCfCGjekyqmdYt1yxfUM5v5SDtaY6tuWik'
  }
};
// All five confirmed stablecoins use 6 decimals — verified per-token via
// Etherscan (USDT/USDC/EURT/EURC) and Tronscan's API (CNHT) alongside the
// TOKEN_CONTRACTS sourcing above, not assumed from convention. A wrong
// decimals value here would show a balance off by a power of ten.
const TOKEN_DECIMALS = { USDT: 6, USDC: 6, EURT: 6, EURC: 6, CNHT: 6 };
// Each stablecoin's peg currency (not necessarily USD) — CNHT pegs to
// **offshore** CNH; frankfurter/ECB only publishes onshore CNY, used here
// as the confirmed, documented approximation (see DECISIONS.md).
const TOKEN_PEG = { USDT: 'USD', USDC: 'USD', EURT: 'EUR', EURC: 'EUR', CNHT: 'CNY' };
// Fallback reporting-currency list used only until the first live
// frankfurter.dev fetch resolves (see fxRates below) — actions.js's
// screens/settings.js then prefers the real, wider set frankfurter
// actually supports (feature-backend.md item 7: "widen beyond G20 to the
// full list available from frankfurter"). Frankfurter/ECB doesn't cover
// every currency this app previously hardcoded here (no SAR, RUB, or ARS,
// notably) — this fallback intentionally mirrors what frankfurter itself
// supports (checked against its /v1/currencies endpoint) rather than the
// old aspirational G20 list, so there's no gap between "what shows before
// the fetch" and "what shows after."
const FALLBACK_REPORTING_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'INR', 'AUD', 'CAD', 'BRL', 'MXN', 'KRW', 'IDR', 'ZAR', 'TRY',
  'CHF', 'CZK', 'DKK', 'HKD', 'HUF', 'ILS', 'ISK', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB'
];
// circleUserRound is the default/first avatar (assigned to every new
// profile — see submitOpenAccount), the animal set is only ever reached by
// changing it afterward in Settings. 'bug' dropped per feature-backend.md
// item 12.
const AVATARS = ['circleUserRound', 'cat', 'dog', 'bird', 'fish', 'rabbit', 'turtle', 'squirrel'];

// Real block explorers, testnet/mainnet aware — replaces a placeholder
// explorer.tynti.app domain (never a real explorer, a leftover from the
// prototype) that home.js/activity.js's "view on chain" links used to
// point at. Ethereum: Blockscout, the same host TX_HISTORY_ENDPOINTS
// already fetches from server-side (tether.worker.js) for transaction
// history, so it's already CSP-allowlisted — a plain <a href> here doesn't
// need CSP's connect-src anyway (that only governs fetch/XHR/WebSocket),
// but reusing an already-vetted, already-keyless host is one less new
// thing to trust. Tron: Tronscan, a human-facing explorer UI (distinct
// from TronGrid's API-only host already used for balances/history). Both
// base URLs and both path schemes verified live via Playwright
// (2026-08-06), not guessed from memory — Blockscout and Tronscan share
// `/address/<addr>` but differ on the transaction path (`/tx/<hash>` vs
// `/transaction/<hash>`), which is why txPath is per-entry, not assumed
// common.
const EXPLORERS = {
  'Ethereum · ERC-20': { testnet: 'https://eth-sepolia.blockscout.com', mainnet: 'https://eth.blockscout.com', txPath: 'tx' },
  'Tron · TRC-20': { testnet: 'https://nile.tronscan.org', mainnet: 'https://tronscan.org', txPath: 'transaction' }
};
function explorerEntry(chain, testnetEnabled) {
  const entry = EXPLORERS[chain];
  if (!entry) return null;
  // Same fail-safe-to-testnet default as tether.worker.js's networkKey()
  // and actions.js's tokenAddressFor — an old/not-yet-updated call site
  // omitting testnetEnabled lands on testnet, never silently on mainnet.
  return { base: testnetEnabled === false ? entry.mainnet : entry.testnet, txPath: entry.txPath };
}
export function explorerAddressUrl(chain, address, testnetEnabled) {
  const entry = explorerEntry(chain, testnetEnabled);
  return entry && address ? `${entry.base}/address/${address}` : null;
}
export function explorerTxUrl(chain, hash, testnetEnabled) {
  const entry = explorerEntry(chain, testnetEnabled);
  return entry && hash ? `${entry.base}/${entry.txPath}/${hash}` : null;
}

// Currency-conversion "deliver as" (feature-swap.md) is only ever offerable
// when the settlement chain resolves to Ethereum — Velora has no Tron
// integration (feature-swap.md's Scope section, Phase 0's investigation).
// One definition, exported, so Send/Transfer's screens and actions.js all
// derive the same answer rather than three independent re-implementations
// of "is this chain even eligible" drifting apart.
export function swapDeliverAvailable(chain) {
  return chain === 'Ethereum · ERC-20';
}

export const CONFIG = { CHAINS, NET, TOKEN_CONTRACTS, TOKEN_DECIMALS, TOKEN_PEG, FALLBACK_REPORTING_CURRENCIES, AVATARS, LANGUAGES };

// The Landing screen itself decides whether to show "welcome back" (a
// passcode prompt) or the new-account form, based on whether any profiles
// exist on this device — so the initial screen is always 'landing'; a
// reload must never skip straight past a passcode prompt into Home.
function loadProfiles() {
  try {
    const raw = localStorage.getItem('tynti_profiles');
    if (raw) {
      const list = JSON.parse(raw);
      // Pre-vault profiles (old passcodeHash/passcodeSalt shape, no
      // `vault`) can't be migrated without the user's original PIN in
      // hand to re-derive a wrap from — dropped deliberately rather than
      // half-migrated; affected devices just re-onboard (see vault.js).
      if (Array.isArray(list)) return list.filter((p) => p && p.vault && p.vault.pin);
    }
  } catch { /* ignore corrupt storage */ }
  return [];
}
function loadActiveProfileId() {
  try { return localStorage.getItem('tynti_active_profile'); } catch { return null; }
}

// User-generated tags per Activity row, keyed by transaction id — kept
// separate from `transactions` itself since transactions are seeded fresh
// in-memory every load (no backend to persist them against), but tags a
// user types are worth keeping across reloads even so.
function loadTxnTags() {
  try {
    const raw = localStorage.getItem('tynti_txn_tags');
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt storage */ }
  return {};
}

// feature-mainnet.md: the one production hostname that defaults to
// mainnet. Every other host (localhost, *.pages.dev previews,
// demo.tynti.com) stays on testnet — CLAUDE.md's "default to testnet in
// local dev" plus a deliberate choice to keep the public demo site and
// any preview/QA deploy off real funds by default. `.gitlab-ci.yml`
// builds `dist/` once and reuses it across preview/production/demo
// deploys, so this has to be a runtime check, not a Vite build-time
// define — the same artifact is served from all of them.
const PRODUCTION_HOSTNAME = 'app.tynti.com';
function defaultTestnetEnabled() {
  try {
    return window.location.hostname !== PRODUCTION_HOSTNAME;
  } catch {
    return true; // no `window` (SSR/test) or blocked access — fail safe to testnet
  }
}

// All of Settings' new preference toggles/dropdowns, bundled into one
// localStorage key rather than one key each — none of them are large or
// need independent versioning, and it keeps loadSettings/persistSettings
// (actions.js) as a single pair instead of eight near-identical ones.
function loadSettings() {
  const defaults = {
    // feature-mainnet.md: production defaults to mainnet, everywhere else
    // (including local dev, per CLAUDE.md) defaults to testnet.
    testnetEnabled: defaultTestnetEnabled(),
    defaultStablecoin: 'USDC', defaultChain: NET.USDC,
    autoLockMinutes: 5 // feature-harden.md 6.2: configurable inactivity timeout
  };
  try {
    const raw = localStorage.getItem('tynti_settings');
    if (raw) return { ...defaults, ...JSON.parse(raw) };
    // No stored settings yet. `persistSettings` (actions.js) only ever
    // runs from a Settings action, never on ordinary boot, so a device
    // that has used the app before but never touched Settings' network
    // toggle would otherwise silently pick up today's default on its
    // next load — for a device that predates this change, that means
    // moving from testnet to mainnet with no confirmation modal, since
    // that modal only fires from the explicit toggle action. `tynti_profiles`
    // (read raw here, not via loadProfiles()'s vault-filtered result — a
    // device can have real, if unmigrated, profile history that filters
    // down to an empty list) existing at all means this device predates
    // this change and gets pinned to the historical default, persisted
    // once so it's a one-time migration rather than a per-load guess. A
    // device with no profile history either way is genuinely new and
    // gets defaultTestnetEnabled()'s real answer.
    if (localStorage.getItem('tynti_profiles') != null) {
      const migrated = { ...defaults, testnetEnabled: true };
      localStorage.setItem('tynti_settings', JSON.stringify(migrated));
      return migrated;
    }
  } catch { /* ignore corrupt storage */ }
  return defaults;
}

// Cached FX rates (feature-backend.md item 7: "cache the last-good rates
// + fetch timestamp; never block the UI on a fresh fetch — serve the
// cached rate immediately, refresh in the background"). Loaded
// synchronously here so a reload has real, if slightly stale, rates
// available on the very first render, not a null/1:1 gap until
// actions.js's ensureFxRates (called at boot, main.js — no profile needs
// to be unlocked for public FX data) resolves.
function loadFxRates() {
  try {
    const raw = localStorage.getItem('tynti_fx_rates');
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt storage */ }
  return null;
}

// 'system' means no explicit override — style.css's
// @media (prefers-color-scheme: dark) block decides. 'light'/'dark' pin it
// regardless of OS preference.
function initialTheme() {
  try {
    const t = localStorage.getItem('tynti_theme');
    if (t === 'light' || t === 'dark') return t;
  } catch { /* storage unavailable */ }
  return 'system';
}

// Persisted language code (feature-ui.md's Language section) — validated
// against the real supported list at read time, not just "is it a
// non-empty string", so a stale/corrupt value from an older build can
// never leave the app stuck on a catalog that doesn't exist. With nothing
// stored yet (first run, or storage unavailable), falls back to matching
// the browser's own language list instead of hardcoding English — an
// explicit Settings change always overwrites this via actions.js's
// setLanguage, which is the only thing that ever writes tynti_language.
function initialLanguage() {
  try {
    const code = localStorage.getItem('tynti_language');
    if (code && LANGUAGES.some((l) => l.code === code)) return code;
  } catch { /* storage unavailable */ }
  if (typeof navigator === 'undefined') return 'en';
  const tags = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language].filter(Boolean);
  return detectBrowserLanguage(tags);
}

// Desktop sidebar's icons-only collapse state (feature-ui.md's Desktop
// section — "Persist sidebar collapsed state in localStorage"). Irrelevant
// below the desktop breakpoint (style.css's media query hides the whole
// affordance there), but harmless to load unconditionally same as theme/
// language above.
function initialSidebarCollapsed() {
  try { return localStorage.getItem('tynti_sidebar_collapsed') === '1'; } catch { return false; }
}

const initialSettings = loadSettings();

let state = {
  screen: 'landing',
  ...initialSettings,
  profiles: loadProfiles(),
  activeProfileId: loadActiveProfileId(),
  landingFormOpen: false,
  landingAddingProfile: false,
  newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
  newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false,
  newProfileErrors: {}, newProfileSubmitting: false,
  lockChecking: false,
  reportingCurrency: 'USD',
  // { rates: { USD: 1, EUR: ..., ... }, date: 'YYYY-MM-DD', fetchedAt: <ms> } | null
  fxRates: loadFxRates(),
  // No seeded demo accounts/history (feature-backend.md item 10 — items
  // 5-6 now supply real balances/transactions once a real account exists).
  // Every account is one this device's profile actually opened via
  // saveOpenAccount (actions.js); there is nothing to "discover" from the
  // chain to repopulate this list — this app's accountId->BIP-44-index
  // scheme (see tether.worker.js's accountIndex()) is a free-text label
  // hashed to an index, not a standard external/change gap-limit scheme,
  // so no on-chain scan could ever recover which indices were previously
  // used as named accounts. Starts empty here (not loaded synchronously
  // like profiles/settings/fxRates above) because it's encrypted per-profile
  // under the session vault key, which only exists post-unlock —
  // actions.js's loadWalletsForSession populates it as part of
  // loadWalletForSession, mirroring loadRecipientsForSession. See
  // DECISIONS.md for why this can't be one global localStorage key the way
  // profiles/settings are: accountIndex() has no per-profile salt, so a
  // global list would let one profile see another's account names/balances.
  wallets: [],
  transactions: [],
  // ccy/chain seed from the "default stablecoin/chain" Settings preference
  // rather than the reporting currency — Receive has no address to show
  // (and greets the user with an empty state) until a real stablecoin is
  // selected, so defaulting to one instead of the fiat reporting currency
  // means a fresh Receive visit is immediately actionable. accountId starts
  // empty (no seeded account exists) — Receive/Send/Transfer each fall back
  // to `wallets[0]` when it doesn't match a real account, and now
  // additionally guard against `wallets` being empty altogether.
  receive: { accountId: '', ccy: initialSettings.defaultStablecoin, chain: initialSettings.defaultChain, addressLoading: false, addressError: null, showDetails: false },
  move: {
    fromAccountId: '', ccy: 'USDC', chain: NET.USDC, toKey: '', amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false,
    // Real network-fee quote (feature-backend.md item 11) for the review/
    // signing-confirmation screen — CLAUDE.md requires fee to be visible
    // before signing. null/loading until fetchReviewFee's async quote
    // resolves; never blocks the form->review transition itself (same
    // "never await from a critical nav path" pattern as
    // ensureAccountBalances).
    reviewFee: null, reviewFeeLoading: false, reviewFeeError: null,
    // 'token' | 'native' | null — which currency reviewFee is actually
    // denominated in (tether.worker.js's quoteTransfer, feature-outstanding.md).
    // A gasless quote's fee comes out of the transferred stablecoin itself,
    // never the chain's native currency — mislabeling it would show e.g.
    // "1.3 ETH" for a fee that's really 1.3 USDT.
    reviewFeeAsset: null,
    // CLAUDE.md: "mainnet actions need a visually distinct, explicit
    // confirmation step" — a real broadcast (feature-backend.md item 11)
    // is exactly that action. Only ever read while testnetEnabled is
    // false; reset every time review is (re-)entered so a stale ack from
    // an earlier pass through the form can't silently carry forward.
    mainnetAck: false,
    // feature-harden.md 6.6: set only after a successful re-auth override
    // of a per-account send limit — validateMove treats the limit as
    // cleared only while the pending move's amount/ccy/account still
    // exactly match what was actually overridden, so editing any of them
    // after an override silently re-requires a fresh one rather than
    // carrying stale approval forward.
    limitOverrideForAmount: null, limitOverrideForCcy: null, limitOverrideForAccountId: null,
    // CCSS-KU's new-recipient cooldown / first-payment echo test
    // (AUDIT.md F3) — same "cleared only while toKey still matches what
    // was actually overridden" pattern as the send-limit override above.
    newRecipientOverrideForToKey: null,
    // Currency-conversion "deliver as" (feature-swap.md, Velora aggregator)
    // — '' means "same as source," today's unchanged behavior; only a
    // non-empty, different-from-`ccy` value activates the swap path.
    // Deliberately reset to '' whenever the source currency changes (see
    // setMoveField/setTransferCcy, actions.js) — a stale deliverCcy from a
    // previous currency choice must not silently carry forward.
    deliverCcy: '',
    // Kept as fields fully separate from reviewFee/reviewFeeAsset above,
    // not overloaded onto them — the review screen shows *both* the plain
    // network-fee row and a distinct "recipient receives ~X CCY" row when
    // a swap is active (feature-swap.md Phase 5/6), and the fee shape
    // itself differs by state (see tether.worker.js's quoteSwapDeliver,
    // feature-swap.md Phase 2): `reviewSwapNeedsApproval` true means the
    // account hasn't approved Velora's router for this token/amount yet —
    // in that state only the approve leg's own fee is knowable
    // (`reviewSwapApproveFee`/`reviewSwapApproveFeeAsset`), the swap's own
    // fee (`reviewSwapFee`/`reviewSwapFeeAsset`) stays null until a
    // follow-up quote after approval succeeds. `reviewSwapOut` (the
    // recipient's expected receive amount) is populated in *both* states —
    // Phase 2's quoteSwapDeliver fetches it via a pure price call
    // (`getRate`) even before an approval exists, so this review row is
    // never left blank on a first-ever swap for a pair.
    reviewSwapOut: null, reviewSwapLoading: false, reviewSwapError: null,
    reviewSwapNeedsApproval: false,
    reviewSwapApproveFee: null, reviewSwapApproveFeeAsset: null,
    reviewSwapFee: null, reviewSwapFeeAsset: null
  },
  savedRecipients: [],
  txnFilter: 'all',
  txnAccountFilter: 'all',
  txnTimeFilter: 'all',
  expandedTxId: null,
  txnTags: loadTxnTags(), // { [txnId]: string[] }
  tagDraft: '', // free-text input for the currently-expanded row's new tag
  showAddRecipient: false, newRecipName: '', newRecipAddress: '', newRecipError: '',
  showOpenAccount: false, newAcctName: '', newAcctOwner: '', newAcctError: '',
  privateKeys: [], // synced from the worker: [{id,label,revealed,words?,backupVerified?,backupPromptDismissed?}]
  showImportKey: false, importText: '', importError: '',
  // Guided backup ceremony (feature-harden.md CCSS-BR): after a reveal,
  // offers to challenge the user on 3 random word positions to confirm
  // they actually wrote the phrase down. Explicitly skippable at every
  // step (user instruction, 2026-08-05) -- never gates anything else in
  // the app; backupVerified/backupPromptDismissed above are informational
  // only, not enforcement.
  showVerifyBackup: false, verifyBackupKeyId: null, verifyBackupIndices: [],
  verifyBackupAnswers: ['', '', ''], verifyBackupError: '',
  showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealPayload: null,
  confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: '',
  lockCode: '', lockError: '',
  // One-click "migrate treasury" auto-sweep (RECOVERY.md / feature-outstanding.md,
  // 2026-08-07) — replaces the manual 4-step workaround documented there
  // with a guided sweep of every non-zero stablecoin balance across every
  // account on this profile to a user-supplied destination address per
  // chain. 'form' (destination address entry) -> 'review' (one screen
  // listing every leg's real fee quote + gas check, CLAUDE.md's
  // per-signature confirmation requirement — see actions.js) -> legs
  // execute sequentially in place on the same review screen (no separate
  // 'success' step; a multi-leg batch's outcome is per-leg, not a single
  // pass/fail).
  showMigrateTreasury: false,
  migrateTreasury: {
    step: 'form',
    toAddress: { 'Ethereum · ERC-20': '', 'Tron · TRC-20': '' },
    errors: {},
    legs: [], // [{ accountId, accountName, ccy, chain, amount, tokenAddress, decimals, toAddress, fee, feeLoading, feeError, nativeOk, status, txHash, errorMsg }]
    mainnetAck: false,
    sending: false
  },
  toast: null,
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  workerConnected: null, // null = not checked yet, true/false once known
  // Per-chain liveness for the Status panel's Blockchains row (feature-
  // backend.md item 9) — null = not checked yet, true/false once known.
  // Keyed the same way NET's values are (chain display name), so the row's
  // render logic can iterate this without a separate name-mapping table.
  chainConnectivity: { 'Ethereum · ERC-20': null, 'Tron · TRC-20': null },
  // Which Status panel row's info explainer is currently open, or null.
  // A single key rather than one showXInfo boolean per row (unlike Home's
  // showBalanceInfo/showActivityInfo, which can both be open on screen at
  // once) — every row here lives in the same small modal, so only one
  // explainer is ever usefully open at a time.
  statusInfoRow: null,
  showStatusPanel: false,
  // Explicit, visually distinct confirmation step for *enabling* mainnet
  // (CLAUDE.md: "Mainnet actions need a visually distinct, explicit
  // confirmation step" — item 11's own text scopes this to "a user opting
  // into mainnet", i.e. this toggle, not every individual signing action;
  // every signing action already gets its own confirmation screen
  // regardless of network). Turning testnet back ON is the safe direction
  // and stays instant. See DECISIONS.md.
  showMainnetConfirm: false,
  showLicense: false,
  showProfileMenu: false,
  showLanguagePicker: false,
  homeEdit: { accountId: null, field: null, draft: '' }, // field: 'name' | 'owner'
  // Cache of derived addresses for Home's per-balance explorer-link icon,
  // keyed by "accountId|ccy" — populated once at boot (see
  // ensureHomeAddresses/main.js), not per-render (a render function must
  // stay synchronous; see app.js's render() comment on reentrant renders).
  homeAddresses: {},
  showBalanceInfo: false,
  showActivityInfo: false,
  recipientEdit: null, // { id, name, address } while editing an existing recipient, else null
  profileEdit: { profileId: null, field: null, draft: '', error: '' }, // field: 'name' | 'email'
  showRecoveryInfo: false,
  swUpdateAvailable: false,
  copyFlash: null, // key of the just-copied button ('address' | 'link'), cleared after a short delay
  accountSort: 'default', // 'default' | 'name-asc' | 'name-desc' | 'balance-desc' | 'balance-asc'
  txnSort: 'date-desc',
  txnSearch: '',
  theme: initialTheme(),
  // The active catalog itself lives in src/i18n/index.js's own module
  // state (t() reads it synchronously on every render); this is just the
  // code driving app.js's dir/lang attributes and Settings' selected
  // option, kept in the store so it participates in the normal
  // render-on-setState cycle like every other preference.
  language: initialLanguage(),
  sidebarCollapsed: initialSidebarCollapsed(),
  // Screen to jump to once the current window's own unlock succeeds — set
  // at boot from a `#screen=X` deep link (main.js), consumed and cleared by
  // unlock()/unlockWithWebauthn() below. Exists because a desktop "open in
  // new window" click (see openInNewWindow, actions.js) opens a *new*
  // browser context with its own Web Worker — the vault key is
  // worker-memory-only and never shared across windows (CLAUDE.md), so the
  // new window always has to re-unlock before it can land on the requested
  // screen rather than skipping straight to it.
  pendingScreen: null,
  // Whether this device/browser has a platform authenticator at all —
  // checked once at boot (main.js) and cached here, since the check itself
  // is async. Combined with a profile's own `vault.webauthn` (does *this*
  // profile have a passkey enrolled) to decide whether Landing's unlock
  // screen offers a biometric option. Not a guarantee prf specifically is
  // supported — that's only known once registerWebauthn actually tries it.
  webauthnAvailable: false
};

// savedRecipients starts empty and is populated post-unlock (see
// loadRecipientsForSession in actions.js) — it's encrypted at rest under
// the active profile's vault key, which doesn't exist yet at module load.
export function getState() {
  return state;
}

export function setState(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  listeners.forEach((fn) => fn(state));
}

// Updates the store without notifying listeners — used for free-text input
// bindings so typing doesn't trigger a full re-render (which would tear
// down the focused <input> node on every keystroke). The value still
// persists in state for the next real render or validation pass.
export function setSilent(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Accepts a number or an exact decimal string (wallets[].balances and
// transactions[].amount are stored as the latter — see toReporting's
// comment below for why) — Number() first so a string operand can't slip
// through to String.prototype.toLocaleString(), which exists and would
// silently return the string unformatted instead of throwing.
export function fmtNum(n) {
  return (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Real currency symbol via the built-in Intl API (feature-backend.md item
// 7's "use the symbol... in the UI" — frankfurter.dev itself has no symbol
// data at all, confirmed against its /v1/currencies endpoint, only ISO
// codes and full names) rather than a hand-maintained CURRENCY_SYMBOLS
// map. Correct for any of frankfurter's ~29 currencies automatically, not
// just a hardcoded subset, and needs no new dependency — Intl is a
// standard Web API in every evergreen browser. For currencies with no
// distinct symbol convention (SEK, PLN, CZK, ...), Intl itself falls back
// to returning the ISO code — detected below so those still render as
// "100.00 SEK" (matching the old map's non-symbol fallback), not "SEK100.00".
function currencySymbol(code) {
  try {
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'symbol' }).formatToParts(0);
    const part = parts.find((p) => p.type === 'currency');
    return part ? part.value : code;
  } catch {
    return code;
  }
}

export function fmtReporting(n) {
  const code = getState().reportingCurrency;
  const symbol = currencySymbol(code);
  return symbol.toUpperCase() === code.toUpperCase() ? fmtNum(n) + ' ' + code : symbol + fmtNum(n);
}

// Converts `amt` units of a stablecoin into the current reporting
// currency using live frankfurter.dev rates (feature-backend.md item 7),
// not the old static FX table. `s.fxRates.rates` is USD-based
// (frankfurter's `base=USD`) with `USD: 1` injected by actions.js's
// ensureFxRates — triangulating through USD is what makes a single
// USD-based rate table enough to convert *from* any peg currency *to* any
// reporting currency in one division, without fetching once per base.
// Before the very first fetch resolves (fxRates is null — briefly, right
// after boot, or if the network is unreachable and no cached rates exist
// yet either), falls back to 1:1 rather than crashing or showing NaN.
//
// `amt` may be a number or an exact decimal string — wallets[].balances
// and transactions[].amount are stored as the latter (see actions.js's
// ensureBalances/refreshTransactions), so their *canonical* value is
// never a lossy float (AUDIT.md finding F2: the worker already returns
// an exact decimal string via ethers' formatUnits(); the old code threw
// that precision away with parseFloat() immediately on ingestion). The
// Number() conversion here is a genuine display/reporting-formatting
// step, not the canonical storage — exactly what ASVS-5 carves out
// ("floats may appear only in display formatting"), unlike the old
// ingestion-time parseFloat() which made the float the stored value.
export function toReporting(ccy, amt) {
  const n = Number(amt);
  const s = getState();
  const rates = s.fxRates && s.fxRates.rates;
  if (!rates) return n;
  const peg = TOKEN_PEG[ccy] || 'USD';
  const reportingRate = rates[s.reportingCurrency];
  const pegRate = rates[peg];
  if (!reportingRate || !pegRate) return n;
  return n * (reportingRate / pegRate);
}

export function totalUSDFor(account) {
  return Object.entries(account.balances).reduce((t, [ccy, amt]) => t + toReporting(ccy, amt), 0);
}

const TXN_SORTS = {
  'date-desc': (a, b) => new Date(b.dateISO) - new Date(a.dateISO),
  'date-asc': (a, b) => new Date(a.dateISO) - new Date(b.dateISO),
  'amount-desc': (a, b) => Math.abs(Number(b.amount)) - Math.abs(Number(a.amount)),
  'amount-asc': (a, b) => Math.abs(Number(a.amount)) - Math.abs(Number(b.amount))
};

// The exact filter/search/sort pipeline the Activity screen renders,
// factored out so CSV/PDF export ("the displayed activity rows") reuses
// this instead of a second copy that could quietly drift out of sync with
// what's actually on screen.
export function filteredTransactions(s) {
  const today = new Date();
  return s.transactions.filter((t) => {
    if (s.txnFilter === 'in' && !(Number(t.amount) > 0)) return false;
    if (s.txnFilter === 'out' && !(Number(t.amount) < 0)) return false;
    if (s.txnAccountFilter !== 'all' && t.accountId !== s.txnAccountFilter) return false;
    if (s.txnTimeFilter !== 'all') {
      const days = (today - new Date(t.dateISO + 'T00:00:00Z')) / 86400000;
      if (s.txnTimeFilter === '1m' && days > 31) return false;
      if (s.txnTimeFilter === '12m' && days > 366) return false;
    }
    return true;
  }).filter((t) => {
    const q = s.txnSearch.trim().toLowerCase();
    if (!q) return true;
    return (t.desc + ' ' + t.wallet + ' ' + t.hash).toLowerCase().includes(q);
  }).sort(TXN_SORTS[s.txnSort] || TXN_SORTS['date-desc']);
}

export function totalReportingAll() {
  return getState().wallets.reduce((t, w) => t + totalUSDFor(w), 0);
}

let toastTimer = null;
export function showToast(msg) {
  setState({ toast: msg });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => setState({ toast: null }), 2200);
}
